import { FormBlock } from '@qbe/ads-blocks-forms';
import { FormGroupLabel, NumericFormat, Typography } from '@qbe/ads-core';
import { PROPERTY_VALUE_MIN } from 'constants/domain-specific';
import React, { useEffect } from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import { useAppContent } from '~/hooks';

import { ContextualHelpWithButton } from '../../components-ds/ContextualHelp/ContextualHelpWithButton';
import { SpecificBusinessDetailsForm } from './types';

export const PropertyValue = () => {
  const { getTextContent, getJSXContent } = useAppContent({
    basePath: 'dsme.businessDetailsPropertyDetails',
    state: {
      propertyValueMin: PROPERTY_VALUE_MIN,
    },
    tokens: {
      propertyValueMin: (state) => state.propertyValueMin,
    },
  });

  const {
    control,
    watch,
    setError,
    clearErrors,
    formState: { errors },
  } = useFormContext<SpecificBusinessDetailsForm>();

  const propertyValue = watch('propertyValue');

  const propertyValueDescription = getJSXContent(
    'contextualhelpers.propertyValueInput.description'
  );

  // Handle warning state for values below minimum threshold
  useEffect(() => {
    const isWarningCondition =
      propertyValue && propertyValue < PROPERTY_VALUE_MIN && propertyValue >= 1;
    const hasWarningError = errors.propertyValue?.type === 'warning';

    if (isWarningCondition && !hasWarningError) {
      setError('propertyValue', {
        type: 'warning',
        message: getTextContent('fields.propertyValueInput.invalidValueMin'),
      });
    } else if (!isWarningCondition && hasWarningError) {
      clearErrors('propertyValue');
    }
  }, [propertyValue, setError, clearErrors, getTextContent, errors.propertyValue?.type]);

  return (
    <>
      <FormGroupLabel
        title={getTextContent('fields.propertyValueInput.title')}
        titleSuffix={
          <ContextualHelpWithButton tracking="help: What is the replacement value of your commercial property?">
            <Typography>{propertyValueDescription}</Typography>
          </ContextualHelpWithButton>
        }
      />

      <FormBlock preset="input" maxWidth="100%">
        <Controller
          control={control}
          render={({ field, fieldState }) => {
            const invalid = Boolean(fieldState.error && fieldState.error.type !== 'warning');
            const isWarning = fieldState.error?.type === 'warning';

            return (
              <NumericFormat
                id="propertyValue"
                name={field.name}
                label={getTextContent('fields.propertyValueInput.label')}
                prefix="$ "
                thousandSeparator
                required
                allowNegative={false}
                onValueChange={(values) => field.onChange(values.floatValue)}
                value={
                  field.value !== null && field.value !== undefined
                    ? String(field.value)
                    : undefined
                }
                decimalScale={0}
                data-track="mask"
                error={invalid}
                warning={isWarning}
                displayIcon
                helperText={
                  fieldState.error?.message ??
                  (fieldState.error?.type === 'required'
                    ? getTextContent('fields.propertyValueInput.invalidTurnovera')
                    : '')
                }
                aria-labelledby="propertyValue"
              />
            );
          }}
          name="propertyValue"
          rules={{
            required: {
              value: true,
              message: getTextContent('fields.propertyValueInput.invalidTurnovera'),
            },
            min: {
              value: 1,
              message: getTextContent('fields.propertyValueInput.invalidPropertyValue'),
            },
          }}
        />
      </FormBlock>
    </>
  );
};
